import { useSyncExternalStore } from "react";
import { getCurrencyBasedOnDomain, getMarketDomain } from "src/utils/domain.util";
import { SHOP_TIME_ZONE, marketTimeZone } from "src/utils/market-time";

const subscribeNever = () => () => {};

/**
 * A browser-only value that is `server` during SSR and hydration and `read()`
 * afterwards, without a setState-in-effect re-render. `read` must return a
 * primitive (or a stable reference).
 */
export function useClientValue<T>(read: () => T, server: T): T {
  return useSyncExternalStore(subscribeNever, read, () => server);
}

/**
 * The same resolution the cart uses: the `?currency=` QA override, else the
 * host's currency, else eur (localhost and unknown hosts). Reading the TLD
 * alone here showed a `?currency=pln` stage walk the eur-only cashback banner.
 */
function currencyForThisShop(): string {
  return getCurrencyBasedOnDomain();
}

/** The shop's currency from its host, or null during SSR and hydration. */
export function useShopCurrency(): string | null {
  return useClientValue<string | null>(currencyForThisShop, null);
}

/**
 * The time zone of the market this shop serves (Warsaw for pl, Bucharest for
 * ro, Ljubljana otherwise), honouring the `?currency=` QA override. Dates the
 * shop promises — a promotion's last day, a credit's expiry — are read in it,
 * not in the viewer's zone. Ljubljana during SSR and hydration.
 */
export function useShopTimeZone(): string {
  return useClientValue<string>(
    () => marketTimeZone(getMarketDomain()),
    SHOP_TIME_ZONE,
  );
}
